import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useMarketingCourses } from './MarketingData';
import { AnimatedSection } from '../ui/AnimatedSection';
import { THUMB_PLACEHOLDER, onThumbError } from '../ui/imageFallback';
import { formatPrice } from '../../lib/format';
import { thumbnailSrc } from '../../lib/imageUrl';

const DEFAULT_HEADING = 'Currently *enrolling*';
const DEFAULT_SUBHEAD =
  'A small catalogue, hand-picked. Browse all programmes or start with the editor’s pick.';

const formatLessons = (n?: number) => (n && n > 0 ? `${n} lessons` : 'Self-paced');

/** Editorial featured-courses block. Asymmetric magazine layout (1 feature + 3 rows). */
export const FeaturedCourses: React.FC = () => {
  const { courses: all, loaded } = useMarketingCourses();
  const courses = all.slice(0, 6);
  const isLoading = !loaded;

  if (isLoading) {
    return (
      <section id="courses" className="py-20 md:py-28 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-12 mb-12">
            <div className="h-12 bg-gray-100 animate-pulse" />
            <div className="space-y-3">
              <div className="h-12 w-2/3 bg-gray-100 animate-pulse" />
              <div className="h-5 w-1/2 bg-gray-100 animate-pulse" />
            </div>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-12">
            <div className="aspect-[4/3] bg-gray-100 animate-pulse" />
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-100 animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (courses.length === 0) {
    return (
      <section id="courses" className="py-20 md:py-28 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary-600 mb-5">
              § Catalogue
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-gray-900 leading-[1.0] tracking-tight mb-5 text-balance">
              Courses coming <em>soon</em>.
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              We're putting the finishing touches on our first programmes. Check back shortly — or sign up to hear when they open.
            </p>
            <Link
              to="/login"
              className="group inline-flex items-center gap-3 px-6 py-3 text-base font-semibold text-gray-900 border-b border-gray-900 hover:text-primary-700 hover:border-primary-700 transition-colors"
            >
              Get notified
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    );
  }

  const [feature, ...rest] = courses;
  const rows = rest.slice(0, 3);

  return (
    <section id="courses" className="py-20 md:py-28 bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-12 mb-12 md:mb-16 items-baseline">
          <div className="text-xs font-mono uppercase tracking-[0.14em] text-gray-500 border-t-2 border-gray-900 pt-3">
            <span className="block text-primary-600 mb-1.5">Nº 03 / Catalogue</span>
            From the catalogue
          </div>
          <div>
            <h2 className="font-serif text-3xl md:text-5xl text-gray-900 leading-[1.05] tracking-tight mb-4 max-w-[18ch] text-balance">
              <DefaultHeadingRender text={DEFAULT_HEADING} />
            </h2>
            <p className="text-lg text-gray-600 max-w-[52ch] leading-relaxed">{DEFAULT_SUBHEAD}</p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-[1.4fr_1fr] gap-10 md:gap-14">
          {/* Feature card — large, magazine-style */}
          <AnimatedSection>
            <Link to={`/course/${feature.id}`} className="group block">
              <div className="aspect-[4/3] bg-gray-100 overflow-hidden mb-6">
                <img
                  src={thumbnailSrc(feature.thumbnailUrl, 'hero') || THUMB_PLACEHOLDER}
                  onError={onThumbError}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  style={{ filter: 'saturate(0.95) contrast(1.02)' }}
                />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-primary-600 mb-3.5">
                Editor's pick · {feature.category || 'New cohort'}
              </div>
              <h3 className="font-serif text-2xl md:text-[34px] leading-[1.05] tracking-[-0.015em] text-gray-900 mb-3.5 max-w-[18ch]">
                {feature.title}
              </h3>
              <p className="text-base md:text-lg text-gray-600 max-w-[56ch] leading-relaxed mb-5">
                {feature.description}
              </p>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200 font-mono text-[11px] uppercase tracking-[0.1em] text-gray-500">
                <span>{feature.level || 'All levels'}</span>
                {feature.instructorName && (
                  <>
                    <span>•</span>
                    <span>With {feature.instructorName}</span>
                  </>
                )}
                {feature.price > 0 && (
                  <span className="ml-auto text-gray-900">{formatPrice(feature.price)}</span>
                )}
              </div>
            </Link>
          </AnimatedSection>

          {/* Row list */}
          <div className="flex flex-col gap-7">
            {rows.map((c, i) => (
              <AnimatedSection key={c.id} delay={0.1 + i * 0.05}>
                <Link
                  to={`/course/${c.id}`}
                  className="group grid grid-cols-[120px_1fr] gap-5 pb-7 border-b border-gray-200 last:border-b-0 last:pb-0"
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={thumbnailSrc(c.thumbnailUrl) || THUMB_PLACEHOLDER}
                      onError={onThumbError}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ filter: 'saturate(0.95)' }}
                    />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-primary-600 mb-2">
                      Nº {String(i + 2).padStart(2, '0')} · {c.level || 'All levels'}
                    </div>
                    <h4 className="font-serif text-lg md:text-xl leading-[1.15] tracking-tight text-gray-900 mb-1.5 group-hover:text-primary-700 transition-colors">
                      {c.title}
                    </h4>
                    <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{c.description}</p>
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.1em] text-gray-500">
                      {formatLessons(c.totalStudents)}
                      {c.price > 0 && ` · From ${formatPrice(c.price)}`}
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-gray-200 flex items-center justify-between gap-4">
          <span className="font-mono text-xs uppercase tracking-[0.12em] text-gray-500">
            Showing {Math.min(courses.length, 4)} of {courses.length}
          </span>
          <Link
            to="/courses"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.12em] text-primary-700 border-b border-primary-600 pb-0.5 hover:text-primary-800 hover:border-primary-800 transition-colors"
          >
            View full catalogue
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/** Tiny inline-em renderer used in the section heading. */
const DefaultHeadingRender: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split(/(\*[^*]+\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('*') && p.endsWith('*') ? <em key={i}>{p.slice(1, -1)}</em> : <React.Fragment key={i}>{p}</React.Fragment>,
      )}
    </>
  );
};
